// Engine — what this deployment nests on, and whose load is on it.
//
// Two tabs answer one question in sequence: what is running (Capacity, K7)
// and what one tenant is set to (Tuning, K8). The third, Flap slabs (V2 #54),
// is platform-wide: the table every flap Mono generates follows.
//
// IT DOES NOT SHOW A FLEET. K1–K3 — the registry, the provisioner, the
// reconciler — are Phase 4. Today there is one endpoint behind a static
// resolver, and a screen that drew a "fleet" of one would be a picture of a
// system that does not exist. The first person to read it would size capacity
// against the picture.
//
// The derivations live in ../capacity.js so they can be executed by
// `node src/capacity.test.js`; this file stays thin enough to read.

import React from 'react';
import { api, errorText } from '../api';
import { useAsync } from '../useAsync';
import { num } from '../format';
import { useToast } from '../ui/Toast';
import {
  capacityRows, enginePathSummary, precedenceOf, sharePct, tuningRows,
} from '../capacity';
import {
  CLEAR_RANKING_CACHE_COPY, clearedText, rankingCacheRows,
} from '../rankingCache';
import {
  APPLIES_TO_NOTE, BOXES_HINT, FAMILIES, FLAP_LABELS, HALF_W, SIGN_OPTIONS,
  USE_BUILTIN_COPY, builtinLines, changesSummary, cloneForm, fromForm,
  newRowFor, previewCell, previewRows, previewSide, removeRowById, rowHints,
  rowProblem, rowStatus, saveAndReload, saveErrorText, setRowCell, statusRows,
  toForm, upsertRow, whoSaved,
} from '../flapSlabs';
import {
  Button, Card, Chips, ConfirmSheet, Empty, ErrorState, Field, Icon, KV, Note,
  PageHead, Pill, RefreshButton, Sheet, Skeleton,
} from '../ui/primitives';

const TABS = [
  { value: 'capacity', label: 'Capacity' },
  { value: 'tuning', label: 'Tuning' },
  { value: 'slabs', label: 'Flap slabs' },
];

const WINDOWS = [
  { value: '1', label: '24h' },
  { value: '7', label: '7d' },
  { value: '30', label: '30d' },
];

/* ── capacity (K7) ──────────────────────────────────────────────────── */

function EnginePath({ health }) {
  const s = enginePathSummary(health);
  return (
    <Card title="Engine path" action={<Pill tone={s.tone}>{s.label}</Pill>}>
      <div className="stack">
        <p className="muted">{s.detail}</p>
        {s.error && <Note tone="danger" title="Endpoint">{s.error}</Note>}
        <KV items={[
          ['Resolver', s.resolver || '—'],
          ['Endpoint', s.endpoint || null],
          ['Engine', s.engineVersion || '—'],
          ['Harness', s.harnessVersion || '—'],
          ['Engine access', s.engineAccess || '—'],
          // null is NOT no. On the remote path the backend cannot see the
          // replica count, so it says so rather than repeating the in-process
          // answer — and neither may this screen.
          ['True parallelism',
            s.trueParallelism === null ? 'Not known from this process'
              : (s.trueParallelism ? 'Yes' : 'No')],
          ['Limited by', s.parallelismLimitedBy || null],
          ['Scale out', s.scaleOut || null],
        ]} />
      </div>
    </Card>
  );
}

// The only cache on the box that holds a decision. Clearing it is for every
// organisation and changes how long the next exports take, never what they
// make. After a clear the card says how many decisions were removed and
// re-reads the stats — it never says "empty", because an export in flight can
// store a decision the moment the clear finishes (see ../rankingCache.js).
function RankingCacheCard() {
  const toast = useToast();
  const stats = useAsync(() => api.rankingCache(), []);
  const [confirm, setConfirm] = React.useState(false);
  const [busy, setBusy] = React.useState(false);
  const [lastClear, setLastClear] = React.useState(null);
  const copy = CLEAR_RANKING_CACHE_COPY;

  async function clear() {
    setBusy(true);
    try {
      const text = clearedText(await api.clearRankingCache());
      setLastClear(text);
      toast.ok(text);
      setConfirm(false);
      stats.reload();
    } catch (e) {
      toast.error(errorText(e));
    } finally { setBusy(false); }
  }

  return (
    <Card
      title="Prepress ranking cache"
      action={<RefreshButton onClick={stats.reload} loading={stats.loading}
                             at={stats.at} />}
    >
      <div className="stack">
        <p className="muted">{copy.what}</p>
        {stats.loading && !stats.data && <Skeleton rows={1} />}
        {stats.error && (
          <ErrorState error={stats.error} text={errorText(stats.error)}
                      onRetry={stats.reload} />
        )}
        {stats.data && <KV items={rankingCacheRows(stats.data)} />}
        {lastClear && <Note title="Last clear">{lastClear}</Note>}
        <div className="row">
          {/* Disabled until the stats load: every refusal the clear can meet
              (an unlocked or absent cache) is already shown by the GET. */}
          <Button variant="danger" disabled={!stats.data}
                  onClick={() => setConfirm(true)}>
            {copy.confirmLabel}
          </Button>
        </div>
      </div>
      {confirm && (
        <ConfirmSheet
          title={copy.title}
          body={copy.body.map((p) => <p key={p}>{p}</p>)}
          confirmLabel={copy.confirmLabel} danger busy={busy}
          onConfirm={clear} onClose={() => setConfirm(false)} />
      )}
    </Card>
  );
}

function CapacityTab({ windowD, onWindow }) {
  const queue = useAsync(() => api.queue(Number(windowD)), [windowD]);
  const health = useAsync(() => api.health(), []);
  const rows = capacityRows(queue.data);
  const totals = queue.data?.totals || {};
  const units = queue.data?.units || {};

  return (
    <div className="stack">
      {health.data && <EnginePath health={health.data} />}

      <Card
        title="Load by organisation"
        action={(
          <div className="row" style={{ gap: 8 }}>
            <Chips options={WINDOWS} value={String(windowD)} onChange={onWindow} />
            <RefreshButton onClick={queue.reload} loading={queue.loading}
                           at={queue.at} />
          </div>
        )}
      >
        {/* §AF. `now` is answered by the process that served the request. That
            is exact with one backend process and becomes a fraction the moment
            a second exists, so it is said here rather than assumed away. When
            D6 moves job state to the shared store, this note goes with it. */}
        <Note tone="" title="What these numbers are">
          <strong>Worker-seconds are not engine time.</strong>{' '}
          {units.worker_seconds
            || 'Pool-worker occupancy, including prepress and ranking.'}{' '}
          Engine calls are counted separately and never blended in —{' '}
          {units.mne_calls || 'calls per plan is what latency is triaged on.'}
          <br />
          <strong>Queued and running are this backend process&rsquo;s view.</strong>{' '}
          With one process that is the whole system; with more than one it is a
          fraction of it, and a capacity decision made on a fraction is wrong in
          the direction that looks fine.
        </Note>

        {queue.loading && !queue.data && <Skeleton rows={4} />}
        {queue.error && <ErrorState error={queue.error} onRetry={queue.reload} />}
        {queue.data && rows.length === 0 && (
          <Empty title="Nothing in this window">
            No jobs ran in the last {windowD === '1' ? '24 hours' : `${windowD} days`},
            and nothing is queued or running right now.
          </Empty>
        )}

        {rows.length > 0 && (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>Organisation</th>
                  <th className="num">Share</th>
                  <th className="num">Worker&nbsp;s</th>
                  <th className="num">Engine calls</th>
                  <th className="num">Jobs</th>
                  <th className="num">Failed</th>
                  <th className="num">Now</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.orgId}>
                    <td>
                      {r.orgName}
                      {r.orgId === '-' && (
                        <span className="muted"> · no tenant recorded</span>
                      )}
                    </td>
                    <td className="num">{sharePct(r.shareOfWorkerSeconds)}</td>
                    <td className="num">{num(Math.round(r.workerSeconds))}</td>
                    <td className="num">
                      {num(r.mneCalls)}
                      {r.shareOfMneCalls !== null && (
                        <span className="muted"> · {sharePct(r.shareOfMneCalls)}</span>
                      )}
                    </td>
                    <td className="num">{num(r.jobs)}</td>
                    <td className="num">{r.failed ? num(r.failed) : '—'}</td>
                    <td className="num">
                      {r.running || r.queued ? (
                        <>
                          {r.running > 0 && <Pill tone="info">{r.running} running</Pill>}
                          {r.queued > 0 && <Pill tone="warn">{r.queued} queued</Pill>}
                        </>
                      ) : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {queue.data && (
          <p className="muted" style={{ marginTop: 10 }}>
            {num(totals.jobs || 0)} jobs · {num(Math.round(totals.worker_seconds || 0))} worker-seconds
            · {num(totals.mne_calls || 0)} engine calls in this window.
          </p>
        )}
      </Card>

      <RankingCacheCard />
    </div>
  );
}

/* ── tuning (K8) ────────────────────────────────────────────────────── */

function TuningTab({ orgId, onOrg }) {
  const toast = useToast();
  const orgs = useAsync(() => api.orgs({ status: 'active' }), []);
  const cfg = useAsync(() => (orgId ? api.orgEngineConfig(orgId) : null), [orgId]);
  const [busy, setBusy] = React.useState(null);

  const rows = tuningRows(cfg.data);
  const { chain, assumed } = precedenceOf(cfg.data);

  async function write(key, value) {
    setBusy(key);
    try {
      await api.patchOrgEngineConfig(orgId, { [key]: value });
      // `null` clears; anything else sets. Saying which happened matters —
      // "saved" would leave an operator unsure whether the org now overrides
      // or has gone back to the shop default.
      toast.ok(value === null ? `${key} cleared — falls back to the shop setting.`
                              : `${key} set for this organisation.`);
      cfg.reload();
    } catch (e) {
      // The backend refuses BY NAME with a reason; showing its message beats
      // any copy written here, which is why errorText falls through to it.
      toast.error(errorText(e));
    } finally { setBusy(null); }
  }

  return (
    <div className="stack">
      <Card title="Organisation">
        <Field label="Tune engine config for">
          <select value={orgId || ''} onChange={(e) => onOrg(e.target.value)}>
            <option value="">Select an organisation…</option>
            {(orgs.data || []).map((o) => (
              <option key={o.id} value={o.id}>{o.name}</option>
            ))}
          </select>
        </Field>
      </Card>

      {!orgId && (
        <Empty title="No organisation selected">
          Engine config is per tenant. Pick one to see what it overrides.
        </Empty>
      )}

      {orgId && cfg.loading && !cfg.data && <Skeleton rows={4} />}
      {orgId && cfg.error && <ErrorState error={cfg.error} onRetry={cfg.reload} />}

      {orgId && cfg.data && (
        <Card title="Engine config">
          <div className="stack">
            {/* The chain is READ from the backend. An order that exists in two
                places drifts, and the copy in the UI is the one nobody tests. */}
            <Note tone={assumed ? 'warn' : ''} title="Precedence, highest first">
              {chain.join('  →  ')}
              {assumed && ' (assumed — the backend published no order)'}
              <br />
              A request beats this organisation; this organisation beats the
              shop-wide setting. Clearing a key here falls back to the shop
              setting, not to nothing.
            </Note>

            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th>Key</th><th>Value</th><th>Where</th><th />
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.key}>
                      <td>{r.key}</td>
                      <td>
                        {r.settable || r.set
                          ? (r.set ? String(r.value) : <span className="muted">not set</span>)
                          : <span className="muted">{r.experiment ? 'experiment' : 'deployment'}</span>}
                      </td>
                      <td>
                        {r.settable
                          ? (r.set ? <Pill tone="info">org</Pill>
                                   : <span className="muted">shop or engine default</span>)
                          : <span className="muted">{r.reason}</span>}
                        {/* The backend says this key has NO EFFECT on the live
                            engine path. Said here, beside the control, because
                            a toggle that silently does nothing is the failure
                            this whole screen exists to prevent. */}
                        {r.notHonoured && (
                          <Note tone="warn" title="Has no effect on this engine path">
                            {r.notHonoured}
                          </Note>
                        )}
                      </td>
                      <td className="num">
                        {r.settable && !r.notHonoured && r.key === 'use_gls' && (
                          <div className="row" style={{ gap: 6, justifyContent: 'flex-end' }}>
                            <Button small loading={busy === r.key}
                                    onClick={() => write(r.key, true)}>On</Button>
                            <Button small loading={busy === r.key}
                                    onClick={() => write(r.key, false)}>Off</Button>
                            {r.set && (
                              <Button small variant="ghost" loading={busy === r.key}
                                      onClick={() => write(r.key, null)}>Clear</Button>
                            )}
                          </div>
                        )}
                        {(r.set && (r.key !== 'use_gls' || r.notHonoured)) && (
                          <Button small variant="ghost" loading={busy === r.key}
                                  onClick={() => write(r.key, null)}>Clear</Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <p className="muted">
              Every change writes an audit event carrying the value before and
              after it. The column holds what is set now; the event log is the
              history.
            </p>
          </div>
        </Card>
      )}
    </div>
  );
}

/* ── screen ─────────────────────────────────────────────────────────── */

/* ── flap slabs (V2 #54) ───────────────────────────────────────────── */

// The table IS the screen: each family lists its rows the way the agreed
// sheet writes them, with a gear on every row. "Add row" and the gear open
// ONE form (a sheet) for ONE row; "Save row" closes it and puts the row back
// into the draft, re-sorted by width, marked new/edited. Nothing reaches jobs
// until "Save table" — the backend validates the table whole, so a row on its
// own could not be saved anyway. (Deepak, 28 Oct: rows appended as forms in
// the list were confusing.)

function NumBox({ value, onChange, placeholder, label }) {
  return (
    <input className="input slab-num" inputMode="decimal" aria-label={label}
           placeholder={placeholder} value={value}
           onChange={(e) => onChange(e.target.value)} />
  );
}

// Every line has the same cells — label, prefix, smallest, "to", largest,
// tail — stacked one line per field inside the form.
function RangeLine({ flapKey, cell, onChange }) {
  const half = HALF_W.includes(flapKey);
  const name = FLAP_LABELS[flapKey];
  return (
    <div className="slab-line">
      <span className="slab-label">{name}</span>
      <span className="slab-prefix">
        {half && (
          <>
            <span className="muted">half the side panel</span>
            <Chips options={SIGN_OPTIONS} value={cell.sign}
                   onChange={(sign) => onChange({ sign })} />
          </>
        )}
      </span>
      <NumBox label={`${name}, smallest`} placeholder="smallest" value={cell.a}
              onChange={(a) => onChange({ a })} />
      <span className="muted">to</span>
      <NumBox label={`${name}, largest`} placeholder="largest" value={cell.b}
              onChange={(b) => onChange({ b })} />
      <span className="slab-tail">
        <span className="slab-unit">mm</span>
        {/* Every line says how it will be saved: "8 to 10 mm", "12 mm" (one
            box filled), "W/2" (both empty on a half-the-side-panel flap). */}
        <span className="slab-reads">= {previewCell(flapKey, cell)}</span>
      </span>
    </div>
  );
}

function RowFormSheet({ fam, editing, onCancel, onSave, onDelete }) {
  const [row, setRow] = React.useState(editing.row);
  const [problem, setProblem] = React.useState(null);
  const set = (key, patch) => { setRow((r) => setRowCell(r, key, patch)); setProblem(null); };
  const hints = rowHints(fam.key, row);
  const adding = editing.mode === 'add';

  function save() {
    const missing = rowProblem(fam.key, row);
    if (missing) { setProblem(missing); return; }
    onSave(row);
  }

  return (
    <Sheet
      title={adding ? 'Add a row' : `Edit row ${editing.index + 1}`}
      subtitle={`${fam.title} — ${fam.sub}`}
      onClose={onCancel}
      footer={(
        <>
          {!adding && (
            <Button variant="danger" onClick={() => onDelete(row.id)}>Delete row</Button>
          )}
          <Button variant="ghost" onClick={onCancel}>Cancel</Button>
          <Button variant="primary" onClick={save}>Save row</Button>
        </>
      )}
    >
      <div className="stack">
        <p className="dim" style={{ margin: 0, fontSize: 12.5 }}>{BOXES_HINT}</p>
        <div className="slab-fields">
          <div className="slab-line">
            <span className="slab-label">Side panel (W)</span>
            <span className="slab-prefix slab-from muted">from</span>
            <NumBox label="Side panel from" placeholder="from" value={row.from}
                    onChange={(v) => set('from', v)} />
            <span className="muted">to</span>
            <NumBox label="Side panel to" placeholder="no limit" value={row.to}
                    onChange={(v) => set('to', v)} />
            <span className="slab-tail">
              <span className="slab-unit">mm</span>
              <span className="dim">leave “to” empty for the last row (“and above”)</span>
            </span>
          </div>
          {fam.flaps.map((k) => (
            <RangeLine key={k} flapKey={k} cell={row[k]} onChange={(p) => set(k, p)} />
          ))}
        </div>
        <div className="slab-summary">
          <div className="slab-caption">This row reads</div>
          <div className="num">
            {previewSide(row)}
            {fam.flaps.map((k) => ` · ${FLAP_LABELS[k]} ${previewCell(k, row[k])}`).join('')}
          </div>
        </div>
        {hints.length > 0 && (
          <Note tone="warn" title="Check this">
            {hints.map((h) => <div key={h}>{h}</div>)}
          </Note>
        )}
        {problem && <Note tone="warn" title="Not ready">{problem}</Note>}
        <p className="dim" style={{ margin: 0, fontSize: 12.5 }}>
          Saving the row updates the table on this page. Jobs keep using the
          table in force until you press “Save table”.
        </p>
      </div>
    </Sheet>
  );
}

function SlabTable({ fam, rows, loaded, onEdit }) {
  if (!rows.length) {
    return (
      <Empty title="No rows yet">
        Add a row for each range of side-panel widths, with the flap sizes for it.
      </Empty>
    );
  }
  const draft = { [fam.key]: rows };
  return (
    <div className="slab-preview-wrap">
      <table className="slab-preview">
        <thead>
          <tr>
            <th>Side panel (W)</th>
            {fam.flaps.map((k) => <th key={k}>{FLAP_LABELS[k]}</th>)}
            <th className="slab-gear-cell" aria-label="Edit" />
          </tr>
        </thead>
        <tbody>
          {previewRows(fam.key, rows).map((r, i) => {
            const status = rowStatus(loaded, draft, fam.key, r.id);
            return (
              <tr key={r.id}>
                <td className="num">
                  {r.side}
                  {status && (
                    <Pill tone={status === 'new' ? 'ok' : 'info'} style={{ marginLeft: 8 }}>
                      {status}
                    </Pill>
                  )}
                </td>
                {r.cells.map((c, j) => <td key={fam.flaps[j]} className="num">{c}</td>)}
                <td className="slab-gear-cell">
                  <button type="button" className="btn ghost icon slab-gear"
                          title="Edit this row"
                          aria-label={`Edit row ${i + 1}: side panels ${r.side}`}
                          onClick={() => onEdit(fam.key, rows[i], i)}>
                    <Icon name="gear" size={18} />
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function SlabFamilyCard({ fam, rows, loaded, onAdd, onEdit }) {
  return (
    <Card title={`${fam.title} — ${fam.sub}`}
          action={(
            <Button small onClick={() => onAdd(fam.key)}>
              <Icon name="plus" size={15} /> Add row
            </Button>
          )}>
      <SlabTable fam={fam} rows={rows} loaded={loaded} onEdit={onEdit} />
    </Card>
  );
}

function FlapSlabsTab() {
  const toast = useToast();
  const loaded = useAsync(() => api.flapSlabs(), []);
  const versions = useAsync(() => api.flapSlabVersions(), []);
  // `base` is the table as loaded; `form` the draft. They share row ids, so
  // the screen can say which rows are new or edited.
  const [base, setBase] = React.useState(null);
  const [form, setForm] = React.useState(null);
  const [editing, setEditing] = React.useState(null);
  const [busy, setBusy] = React.useState(false);
  const [problem, setProblem] = React.useState(null);
  const [confirm, setConfirm] = React.useState(false);
  const view = loaded.data;

  function reset(table) {
    const f = toForm(table);
    setBase(f);
    setForm(cloneForm(f));
    setEditing(null);
    setProblem(null);
  }

  React.useEffect(() => { if (view) reset(view.table); }, [view]);

  const changes = changesSummary(base, form);
  const dirty = changes.total > 0;

  function openAdd(famKey) {
    setEditing({ famKey, mode: 'add', row: newRowFor(form, famKey) });
  }

  function openEdit(famKey, row, index) {
    setEditing({ famKey, mode: 'edit', row: cloneForm(row), index });
  }

  function saveRow(row) {
    setForm((f) => upsertRow(f, editing.famKey, row));
    setEditing(null);
    setProblem(null);
  }

  function deleteRow(id) {
    setForm((f) => removeRowById(f, editing.famKey, id));
    setEditing(null);
    setProblem(null);
  }

  async function save(table) {
    setBusy(true);
    setProblem(null);
    try {
      const out = await saveAndReload(api, table, view.version);
      reset(out.view.table);
      toast.ok(out.message);
      setConfirm(false);
      loaded.reload();
      versions.reload();
    } catch (e) {
      setProblem(saveErrorText(e, errorText));
    } finally { setBusy(false); }
  }

  function saveTable() {
    let table;
    try { table = fromForm(form); } catch (e) { setProblem(saveErrorText(e, errorText)); return; }
    save(table);
  }

  if (loaded.error) {
    return <ErrorState error={loaded.error} text={errorText(loaded.error)} onRetry={loaded.reload} />;
  }
  if (!view || !form) return <Skeleton rows={4} />;

  const editingFam = editing && FAMILIES.find((f) => f.key === editing.famKey);
  const actions = (
    <div className="row row-wrap">
      <Button variant="primary" loading={busy} disabled={!dirty} onClick={saveTable}>
        Save table
      </Button>
      <Button variant="ghost" disabled={!dirty || busy} onClick={() => reset(view.table)}>
        Discard changes
      </Button>
      {view.table && (
        <Button variant="danger" disabled={busy} onClick={() => setConfirm(true)}>
          {USE_BUILTIN_COPY.confirmLabel}
        </Button>
      )}
    </div>
  );

  return (
    <div className="stack">
      <Card title="Flap slabs"
            action={<RefreshButton onClick={loaded.reload} loading={loaded.loading} at={loaded.at} />}>
        <div className="stack">
          <KV items={statusRows(view)} />
          <Note>{APPLIES_TO_NOTE}</Note>
          {!view.engine_supports_tables && (
            <Note tone="warn" title="This backend cannot apply a table yet">
              Its engine package predates flap-slab tables. Deploy the newer
              engine package before saving one.
            </Note>
          )}
          {dirty && (
            <Note tone="warn" title="Not live yet">
              {changes.text}. Jobs keep using the table in force until you press
              “Save table”.
            </Note>
          )}
          {problem && <Note tone="warn" title="Not saved">{problem}</Note>}
          {actions}
        </div>
      </Card>

      {FAMILIES.map((fam) => (
        <SlabFamilyCard key={fam.key} fam={fam} rows={form[fam.key]} loaded={base}
                        onAdd={openAdd} onEdit={openEdit} />
      ))}

      {/* The same actions where the tables end, while there is something to
          save, so editing the second family never means scrolling back up. */}
      {dirty && (
        <Card>
          <div className="stack">
            <div className="muted">{changes.text} — not live yet.</div>
            {problem && <Note tone="warn" title="Not saved">{problem}</Note>}
            {actions}
          </div>
        </Card>
      )}

      <Card title="Built-in slabs (what Mono uses with no table)">
        {builtinLines(view.builtin).length
          ? <ul className="stack" style={{ margin: 0, paddingLeft: 18 }}>
              {builtinLines(view.builtin).map((l) => <li key={l}>{l}</li>)}
            </ul>
          : <Empty title="Not described by this engine package" />}
      </Card>

      <Card title="History"
            action={<RefreshButton onClick={versions.reload} loading={versions.loading} at={versions.at} />}>
        {(versions.data?.versions || []).length === 0
          ? <Empty title="No table has been saved yet" />
          : (
            <div className="slab-preview-wrap">
              <table className="slab-preview">
                <thead><tr><th>Version</th><th>Saved</th><th>By</th><th>Table</th></tr></thead>
                <tbody>
                  {versions.data.versions.map((v) => (
                    <tr key={v.version}>
                      <td className="num">{v.version}</td>
                      <td>{v.created_at}</td>
                      <td>{whoSaved(v.created_by_name, v.created_by) || <span className="dim">—</span>}</td>
                      <td>{v.cleared ? <Pill>built-in slabs</Pill> : <Pill tone="info">{(v.digest || '').slice(0, 12)}</Pill>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
      </Card>

      {editing && (
        <RowFormSheet fam={editingFam} editing={editing}
                      onCancel={() => setEditing(null)} onSave={saveRow} onDelete={deleteRow} />
      )}

      {confirm && (
        <ConfirmSheet
          title={USE_BUILTIN_COPY.title}
          body={USE_BUILTIN_COPY.body.map((p) => <p key={p}>{p}</p>)}
          confirmLabel={USE_BUILTIN_COPY.confirmLabel} danger busy={busy}
          onConfirm={() => save(null)} onClose={() => setConfirm(false)} />
      )}
    </div>
  );
}

export default function Engine({ query }) {
  const [tab, setTab] = React.useState(
    ['tuning', 'slabs'].includes(query?.tab) ? query.tab : 'capacity');
  const [windowD, setWindowD] = React.useState('7');
  const [orgId, setOrgId] = React.useState(query?.org || '');

  return (
    <div className="stack">
      <PageHead
        title="Engine"
        sub="What this deployment nests on, and whose load is on it."
        action={<Chips options={TABS} value={tab} onChange={setTab} />}
      />
      {tab === 'capacity' && <CapacityTab windowD={windowD} onWindow={setWindowD} />}
      {tab === 'tuning' && <TuningTab orgId={orgId} onOrg={setOrgId} />}
      {tab === 'slabs' && <FlapSlabsTab />}
    </div>
  );
}
